import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../utils/store";
import { Icon } from "../components/tailwind";
import { Button } from "../components/tailwind";
import { Dropdown } from "../components/tailwind";
import { Tab } from "../components/Tab";
import { UploadSettings, UploadSettingsData } from "./UploadSettings";
import { UploadButton } from "./UploadButton";

type Product = {
  inriver_id: number;
  product_id: string | null;
  external_id: string | null;
  name: string | null;
  status: "new" | "existing";
  field_values: Record<string, unknown>;
  inriver_data: any;
};

type FetchResponse = {
  success: boolean;
  products: Product[];
  total: number;
  conflicting_ids: number[];
  failed_ids: string[];
  error?: string;
};

type IdentifierOption = {
  value: string;
  label: string;
  is_default: boolean;
};

type Workarea = {
  id: string;
  name: string;
  is_query: boolean;
};

type Subscription = {
  id: number;
  workarea_id: string;
  workarea_name: string;
  settings: UploadSettingsData;
  last_run_at: string | null;
};

type Props = {
  customerSlug?: string;
};

const getInitialProductIds = (): string => {
  const params = new URLSearchParams(window.location.search);
  const pimIds = params.get("pim_ids");
  return pimIds || "";
};

const apiHeaders = (token: string | null): HeadersInit => ({
  "Content-Type": "application/json",
  token: token || "",
});

export const ImportDocumentsFromPim: React.FC<Props> = ({ customerSlug }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [productIds, setProductIds] = useState<string>(getInitialProductIds);
  const [identifierType, setIdentifierType] = useState<string>("sysId");
  const [identifierOptions, setIdentifierOptions] = useState<
    IdentifierOption[]
  >([{ value: "sysId", label: "System ID", is_default: true }]);
  const [workareas, setWorkareas] = useState<Workarea[]>([]);
  const [selectedWorkarea, setSelectedWorkarea] = useState<string>("");
  const [loadingWorkareas, setLoadingWorkareas] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscribing, setSubscribing] = useState<boolean>(false);
  const [subscribeWorkarea, setSubscribeWorkarea] = useState<string>("");
  const [
    subscribeSettings,
    setSubscribeSettings,
  ] = useState<UploadSettingsData | null>(null);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    number | null
  >(null);
  const [editSettings, setEditSettings] = useState<UploadSettingsData | null>(
    null
  );
  const [savingEdit, setSavingEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<FetchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<UploadSettingsData | null>(null);
  const [inriverIntegrationEnabled, setInriverIntegrationEnabled] = useState<
    boolean
  >(false);
  const [loadingFormData, setLoadingFormData] = useState<boolean>(true);

  // Guards the fire-and-forget mount fetches from updating state after unmount
  // (e.g. navigating away during the workarea list fetch).
  const mounted = useRef(true);
  useEffect(() => {
    return (): void => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pimIds = params.get("pim_ids");
    if (pimIds) {
      setProductIds(pimIds);
    }
  }, []);

  // Fetch all form data (including inriver settings) on mount
  useEffect(() => {
    const fetchFormData = async (): Promise<void> => {
      if (!token) return;
      try {
        const response = await fetch(
          `/api/_internal/extract/upload_form_choices`,
          { headers: apiHeaders(token) }
        );
        if (!mounted.current) return;

        if (response.ok) {
          const data = await response.json();
          if (!mounted.current) return;
          setInriverIntegrationEnabled(
            data.inriver_integration_enabled || false
          );
          setIdentifierOptions(data.identifier_options || []);
          setIdentifierType(data.default_identifier || "sysId");
        } else {
          setError("Could not fetch form data. Please refresh the page.");
        }
      } catch (err) {
        if (mounted.current) {
          setError("Could not fetch form data. Please refresh the page.");
        }
      } finally {
        if (mounted.current) setLoadingFormData(false);
      }
    };

    fetchFormData();
  }, [token]);

  // Fetch the available workareas once the integration is confirmed enabled
  useEffect(() => {
    const fetchWorkareas = async (): Promise<void> => {
      if (!token || !inriverIntegrationEnabled) return;
      setLoadingWorkareas(true);
      try {
        const response = await fetch(
          `/api/_internal/extract/inriver_workareas`,
          { headers: apiHeaders(token) }
        );
        if (!mounted.current) return;

        if (response.ok) {
          const data = await response.json();
          if (!mounted.current) return;
          setWorkareas(data.workareas || []);
        } else {
          setError("Could not fetch workareas. Please refresh the page.");
        }
      } catch (err) {
        if (mounted.current) {
          setError("Could not fetch workareas. Please refresh the page.");
        }
      } finally {
        if (mounted.current) setLoadingWorkareas(false);
      }
    };

    fetchWorkareas();
  }, [token, inriverIntegrationEnabled]);

  // Fetch existing nightly subscriptions once the integration is enabled
  const fetchSubscriptions = async (): Promise<void> => {
    if (!token || !inriverIntegrationEnabled) return;
    try {
      const response = await fetch(
        `/api/_internal/extract/workarea_subscriptions`,
        { headers: apiHeaders(token) }
      );
      if (response.ok) {
        const data = await response.json();
        if (!mounted.current) return;
        setSubscriptions(data.subscriptions || []);
      }
    } catch (err) {
      // Non-critical: the subscribe list just stays empty.
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [token, inriverIntegrationEnabled]);

  // Fetch product previews from inRiver for a set of identifiers.
  // Both tabs end up here so the results below the tabs stay identical.
  const fetchPreview = async (
    identifiers: string,
    identifier: string
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(
        `/api/_internal/extract/fetch_from_inriver`,
        {
          method: "POST",
          headers: apiHeaders(token),
          body: JSON.stringify({
            product_ids: identifiers,
            identifier_type: identifier,
          }),
        }
      );

      const data: FetchResponse = await response.json();

      if (!response.ok || data.error) {
        setError(data.error || "Failed to fetch products");
        setLoading(false);
        return;
      }

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (): Promise<void> => {
    if (!productIds.trim()) {
      setError("Please enter at least one product ID");
      return;
    }

    if (!token) {
      setError("Authentication required. Please refresh the page.");
      return;
    }

    await fetchPreview(productIds, identifierType);
  };

  const handleGetEntities = async (): Promise<void> => {
    if (!selectedWorkarea) {
      setError("Please select a workarea");
      return;
    }

    if (!token) {
      setError("Authentication required. Please refresh the page.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(
        `/api/_internal/extract/inriver_workareas/${selectedWorkarea}/entities`,
        { headers: apiHeaders(token) }
      );

      const data = await response.json();

      if (!response.ok || data.error) {
        setError(data.error || "Failed to fetch workarea entities");
        setLoading(false);
        return;
      }

      const entityIds: number[] = data.entity_ids || [];
      if (entityIds.length === 0) {
        setError("The selected workarea contains no entities");
        setLoading(false);
        return;
      }

      await fetchPreview(entityIds.join(","), "sysId");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  const handleTabChange = (): void => {
    setResult(null);
    setError(null);
  };

  // The subscription endpoints all answer with either {"success": true, ...}
  // or {"error": "..."}, so the send/parse/report shape is shared. Returns the
  // parsed body, or null once the failure has been surfaced to the user.
  const callSubscriptionsApi = async (
    url: string,
    init: RequestInit,
    failureMessage: string
  ): Promise<Record<string, any> | null> => {
    try {
      const response = await fetch(url, {
        ...init,
        headers: apiHeaders(token),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setError(data.error || failureMessage);
        return null;
      }
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return null;
    }
  };

  const handleSubscribe = async (): Promise<void> => {
    if (!subscribeWorkarea || !subscribeSettings || !token) return;
    const workarea = workareas.find((w) => w.id === subscribeWorkarea);
    setSubscribing(true);
    const data = await callSubscriptionsApi(
      `/api/_internal/extract/workarea_subscriptions`,
      {
        method: "POST",
        body: JSON.stringify({
          workarea_id: subscribeWorkarea,
          workarea_name: workarea ? workarea.name : subscribeWorkarea,
          settings: subscribeSettings,
        }),
      },
      "Failed to subscribe"
    );
    setSubscribing(false);
    if (!data) return;
    // Use the created subscription from the response rather than refetching:
    // only the server knows the new id.
    const created: Subscription = data.subscription;
    setSubscriptions((subs) => [
      ...subs.filter((s) => s.id !== created.id),
      created,
    ]);
  };

  // UploadSettings reports its own state on mount, which is what fills
  // editSettings — seeding it here would only be overwritten. Clearing it
  // keeps Save disabled until the newly mounted form has reported, so a blob
  // belonging to the previously edited row can never be saved onto this one.
  const handleStartEdit = (subscriptionId: number): void => {
    setError(null);
    setEditingSubscriptionId(subscriptionId);
    setEditSettings(null);
  };

  const closeEditor = (): void => {
    setEditingSubscriptionId(null);
    setEditSettings(null);
  };

  const handleSaveEdit = async (subscriptionId: number): Promise<void> => {
    if (!editSettings || !token) return;
    const saved = editSettings;
    setSavingEdit(true);
    const data = await callSubscriptionsApi(
      `/api/_internal/extract/workarea_subscriptions/${subscriptionId}`,
      { method: "PATCH", body: JSON.stringify({ settings: saved }) },
      "Failed to save subscription"
    );
    setSavingEdit(false);
    if (!data) return;
    setSubscriptions((subs) =>
      subs.map((s) => (s.id === subscriptionId ? { ...s, settings: saved } : s))
    );
    closeEditor();
  };

  const handleUnsubscribe = async (subscriptionId: number): Promise<void> => {
    if (!token) return;
    const data = await callSubscriptionsApi(
      `/api/_internal/extract/workarea_subscriptions/${subscriptionId}`,
      { method: "DELETE" },
      "Failed to unsubscribe"
    );
    if (!data) return;
    setSubscriptions((subs) => subs.filter((s) => s.id !== subscriptionId));
  };

  const handleSubmitStart = (): void => {
    setSubmitting(true);
  };

  const handleSubmitError = (error: string): void => {
    alert(error);
    setSubmitting(false);
  };

  // Construct the upload-with-pim URL
  const getUploadWithPimUrl = (): string => {
    // Build the URL using the customer slug
    if (customerSlug) {
      return `/d/${customerSlug}/wizard/upload-with-pim/`;
    }
  };

  const foundCount = result ? result.products.length : 0;
  const notFoundCount = result ? result.failed_ids.length : 0;
  const canSubmit =
    result && result.products.length > 0 && settings && !submitting;

  if (loadingFormData) {
    return (
      <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-red-600"></div>
      </div>
    );
  }

  // Shared workarea dropdown used by both the "Fetch workarea" and
  // "Subscribe to workarea" panes.
  const workareaOptions = workareas.map((workarea) => ({
    key: workarea.id,
    value: workarea.id,
    text: workarea.is_query ? `${workarea.name} (query)` : workarea.name,
  }));

  const renderWorkareaDropdown = (
    value: string,
    onChange: (value: string) => void,
    disabled: boolean,
    testIdBase: string
  ): JSX.Element => (
    <div className="tw-mb-6 tw-max-w-xs">
      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
        Workarea
      </label>
      <Dropdown
        options={workareaOptions}
        value={value}
        onChange={(selected): void => onChange(selected as string)}
        placeholder={
          loadingWorkareas ? "Loading workareas..." : "Select a workarea"
        }
        disabled={disabled}
        testIdBase={testIdBase}
      />
    </div>
  );

  const fetchIdsPane = (
    <div>
      <p className="tw-text-gray-500 tw-mb-6">
        Search for products by entering identifiers separated by commas or
        newlines
      </p>

      {/* Identifier Type Dropdown */}
      <div className="tw-mb-4 tw-max-w-xs">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
          Search by
        </label>
        <Dropdown
          options={identifierOptions.map((option) => ({
            key: option.value,
            value: option.value,
            text: option.label,
          }))}
          value={identifierType}
          onChange={(selected): void => {
            setIdentifierType(selected as string);
            setResult(null); // Clear previous results when changing identifier type
            setError(null);
          }}
          disabled={loading}
          testIdBase="identifier"
        />
      </div>

      <div className="tw-mb-6">
        <textarea
          data-testid="product-ids-input"
          value={productIds}
          onChange={(e): void => setProductIds(e.target.value)}
          placeholder={`Enter ${
            identifierType === "sysId"
              ? "System IDs"
              : `${identifierType} values`
          } separated by commas or newlines (e.g. ${
            identifierType === "sysId" ? "123, 456, 789" : "ABC-123, XYZ-456"
          })`}
          rows={8}
          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-sm tw-p-3 tw-font-mono tw-resize-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 focus:tw-border-transparent"
          disabled={loading}
        />
      </div>

      <div>
        <Button
          onClick={handleSearch}
          disabled={loading || !productIds.trim()}
          variant="primary"
          size="lg"
        >
          {loading ? "Searching..." : "Search"}
        </Button>
      </div>
    </div>
  );

  const fetchWorkareaPane = (
    <div>
      <p className="tw-text-gray-500 tw-mb-6">
        Select a workarea to import all the entities it contains
      </p>

      {renderWorkareaDropdown(
        selectedWorkarea,
        (value): void => {
          setSelectedWorkarea(value);
          setResult(null); // Clear previous results when changing workarea
          setError(null);
        },
        loading || loadingWorkareas,
        "workarea"
      )}

      <div>
        <Button
          onClick={handleGetEntities}
          disabled={loading || loadingWorkareas || !selectedWorkarea}
          variant="primary"
          size="lg"
        >
          {loading ? "Fetching..." : "Get entities"}
        </Button>
      </div>
    </div>
  );

  const subscribePane = (
    <div>
      <p className="tw-text-gray-500 tw-mb-6">
        Subscribe to a workarea to automatically import its entities every
        night, using the settings below
      </p>

      {renderWorkareaDropdown(
        subscribeWorkarea,
        (value): void => {
          setSubscribeWorkarea(value);
          setError(null);
        },
        subscribing || loadingWorkareas,
        "subscribe-workarea"
      )}

      {/* Import settings stored on the subscription */}
      {subscribeWorkarea && (
        <div className="tw-mb-6">
          <UploadSettings
            customerSlug={customerSlug}
            onChange={setSubscribeSettings}
          />
        </div>
      )}

      <div>
        <Button
          onClick={handleSubscribe}
          disabled={subscribing || !subscribeWorkarea || !subscribeSettings}
          variant="primary"
          size="lg"
        >
          {subscribing ? "Subscribing..." : "Subscribe"}
        </Button>
      </div>

      {/* Existing nightly subscriptions */}
      {subscriptions.length > 0 && (
        <div className="tw-mt-8">
          <h3 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Nightly subscriptions
          </h3>
          <ul className="tw-border tw-border-gray-200 tw-rounded-sm tw-divide-y tw-divide-gray-100">
            {subscriptions.map((subscription) => (
              <li key={subscription.id} className="tw-px-3 tw-py-2 tw-text-sm">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-gray-700">
                    {subscription.workarea_name}
                  </span>
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <button
                      type="button"
                      onClick={(): void => {
                        if (editingSubscriptionId === subscription.id) {
                          closeEditor();
                        } else {
                          handleStartEdit(subscription.id);
                        }
                      }}
                      className="tw-text-gray-600 hover:tw-underline"
                    >
                      {editingSubscriptionId === subscription.id
                        ? "Close"
                        : "Edit"}
                    </button>
                    <button
                      type="button"
                      onClick={(): void => {
                        handleUnsubscribe(subscription.id);
                      }}
                      className="tw-text-red-600 hover:tw-underline"
                    >
                      Unsubscribe
                    </button>
                  </div>
                </div>

                {/* Editing mounts UploadSettings with the stored settings so the
                    current choices (agent, prompt, ...) are visible up front. */}
                {editingSubscriptionId === subscription.id && (
                  <div className="tw-mt-4">
                    <UploadSettings
                      customerSlug={customerSlug}
                      initialSettings={subscription.settings}
                      onChange={setEditSettings}
                    />
                    <div className="tw-mt-4 tw-flex tw-items-center tw-gap-3">
                      <Button
                        onClick={(): void => {
                          handleSaveEdit(subscription.id);
                        }}
                        disabled={savingEdit || !editSettings}
                        variant="primary"
                      >
                        {savingEdit ? "Saving..." : "Save"}
                      </Button>
                      <button
                        type="button"
                        onClick={closeEditor}
                        className="tw-text-gray-600 hover:tw-underline"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-space-y-8">
      {/* PIM Search Section */}
      {inriverIntegrationEnabled ? (
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-mb-4">Import from PIM</h1>
          <Tab
            queryParamKey="pim_tab"
            onChange={handleTabChange}
            panes={[
              {
                menuItem: {
                  key: "ids",
                  icon: <i className="material-icons">format_list_numbered</i>,
                  heading: "Fetch IDs",
                },
                content: fetchIdsPane,
              },
              {
                menuItem: {
                  key: "workarea",
                  icon: <i className="material-icons">folder_open</i>,
                  heading: "Fetch workarea",
                },
                content: fetchWorkareaPane,
              },
              {
                menuItem: {
                  key: "subscribe",
                  icon: <i className="material-icons">schedule</i>,
                  heading: "Subscribe to workarea",
                },
                content: subscribePane,
              },
            ]}
          />

          {result && result.products.length > 0 && (
            <div className="tw-mt-6 tw-flex tw-items-center tw-gap-4 tw-text-sm">
              <span className="tw-font-medium">
                {result.products.length} Total IDs
              </span>
              <span className="tw-text-green-600 tw-font-medium">
                {foundCount} Found
              </span>
              <span className="tw-text-red-600 tw-font-medium">
                {notFoundCount} Not Found
              </span>
            </div>
          )}
        </div>
      ) : (
        <h1 className="tw-text-center tw-my-auto tw-text-gray-500">
          This feature requires an active Inriver integration. Contact
          support@textual.ai for assistance
        </h1>
      )}

      {/* Error Display */}
      {error && (
        <div className="tw-mb-6 tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-sm">
          <p className="tw-text-red-800 tw-font-medium">Error</p>
          <p className="tw-text-red-600">{error}</p>
        </div>
      )}

      {/* Search Results */}
      {result && (
        <div className="tw-mt-8 tw-grid tw-grid-cols-2 tw-gap-6">
          {/* Found IDs */}
          {result.products.length > 0 && (
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-4 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                <Icon name="check_circle" className="tw-text-green-600" />
                <h3 className="tw-text-lg tw-font-medium tw-text-green-600">
                  Found IDs
                </h3>
              </div>
              <div className="tw-max-h-64 tw-overflow-y-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead className="tw-bg-gray-50 tw-sticky tw-top-0">
                    <tr>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        InRiver ID
                      </th>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        Product ID
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.products.map((product, index) => (
                      <tr
                        key={product.inriver_id}
                        className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 ${
                          index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"
                        }`}
                      >
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-700">
                          {product.inriver_id}
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-700">
                          {product.external_id || product.product_id || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Not Found IDs */}
          {result.failed_ids.length > 0 && (
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-4 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                <Icon name="cancel" className="tw-text-red-600" />
                <h3 className="tw-text-lg tw-font-medium tw-text-red-600">
                  Not Found IDs
                </h3>
              </div>
              <div className="tw-max-h-64 tw-overflow-y-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead className="tw-bg-gray-50 tw-sticky tw-top-0">
                    <tr>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        InRiver ID
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.failed_ids.map((id, index) => (
                      <tr
                        key={id}
                        className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 ${
                          index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"
                        }`}
                      >
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-600">
                          {id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Settings - Only show if we have results */}
      {result && result.products.length > 0 && (
        <div className="tw-border-t tw-border-gray-200 tw-pt-8">
          <h2 className="tw-text-xl tw-font-semibold tw-mb-4 tw-text-center">
            Upload Settings
          </h2>
          <UploadSettings customerSlug={customerSlug} onChange={setSettings} />
        </div>
      )}

      {/* Submit Button - Only show if we have results and settings */}
      {result && result.products.length > 0 && settings && (
        <div className="tw-flex tw-justify-center tw-mt-6 tw-border-t tw-border-gray-200 tw-pt-6">
          <UploadButton
            disabled={!canSubmit}
            submitting={submitting}
            readyText={`Import ${foundCount} Products`}
            submittingText="Importing..."
            actionUrl={getUploadWithPimUrl()}
            settings={settings}
            productIds={result.products.map((p) => p.inriver_id)}
            onSubmitStart={handleSubmitStart}
            onSubmitError={handleSubmitError}
          />
        </div>
      )}
    </div>
  );
};
